import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, UserX, UserCheck, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import PageHeader from "@/components/admin/PageHeader";
import StatusBadge from "@/components/admin/StatusBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 20;

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input by 350ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir };
      if (debouncedSearch) params.search = debouncedSearch;
      if (roleFilter) params.role = roleFilter;
      if (activeFilter !== "") params.is_active = activeFilter === "true";
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, roleFilter, activeFilter, sortBy, sortDir]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_active: !u.is_active });
      toast.success(`User ${u.is_active ? "deactivated" : "activated"}`);
      loadUsers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("User removed");
      loadUsers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminGuard>
      <AdminLayout>
        <PageHeader title="User Management" subtitle={`${total} total users`} />

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-4 mb-6 flex flex-wrap gap-3 items-center shadow-sm">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--zt-charcoal))]/40" />
            <input
              type="text"
              placeholder="Search name or email…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 h-9 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50 font-body"
            />
          </div>
          <select
            value={roleFilter}
            onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-xs border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50 font-heading uppercase tracking-wider"
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="advisor">Advisor</option>
            <option value="admin">Admin</option>
          </select>
          <select
            value={activeFilter}
            onChange={e => { setActiveFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-xs border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50 font-heading uppercase tracking-wider"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select
            value={`${sortBy}:${sortDir}`}
            onChange={e => { const [sb, sd] = e.target.value.split(":"); setSortBy(sb); setSortDir(sd); setPage(1); }}
            className="h-9 px-3 text-xs border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50 font-heading uppercase tracking-wider"
          >
            <option value="created_at:desc">Newest First</option>
            <option value="created_at:asc">Oldest First</option>
            <option value="name:asc">Name A–Z</option>
            <option value="email:asc">Email A–Z</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr className="border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
                  {["Name / Email", "Country", "Provider", "Role", "Status", "Joined", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3.5 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-[hsl(var(--zt-beige-deep))]/50 animate-pulse">
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-4 py-4"><div className="h-4 bg-[hsl(var(--zt-beige-deep))]/50 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-[hsl(var(--zt-charcoal))]/40">No users found</td></tr>
                ) : users.map(u => (
                  <tr key={u.user_id} className="border-b border-[hsl(var(--zt-beige-deep))]/50 hover:bg-[hsl(var(--zt-beige))]/30 transition-colors">
                    <td className="px-4 py-3.5">
                      <p className="font-semibold text-sm text-[hsl(var(--zt-teal))]">{u.name || "—"}</p>
                      <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55 mt-0.5">{u.email}</p>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-[hsl(var(--zt-charcoal))]/70">{u.country || "—"}</td>
                    <td className="px-4 py-3.5 text-xs text-[hsl(var(--zt-charcoal))]/55 capitalize">{u.auth_provider}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={u.role} /></td>
                    <td className="px-4 py-3.5"><StatusBadge status={u.is_active ? "active" : "inactive"} /></td>
                    <td className="px-4 py-3.5 text-[11px] text-[hsl(var(--zt-charcoal))]/55 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/users/${u.user_id}`)}
                          className="w-7 h-7 rounded-lg bg-[hsl(var(--zt-beige))] grid place-items-center text-[hsl(var(--zt-teal))]/60 hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige-deep))] transition-colors"
                          title="View details"
                        >
                          <Eye size={13} />
                        </button>
                        <button
                          onClick={() => toggleActive(u)}
                          className={`w-7 h-7 rounded-lg grid place-items-center transition-colors ${u.is_active ? "bg-amber-50 text-amber-600 hover:bg-amber-100" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}
                          title={u.is_active ? "Deactivate" : "Activate"}
                        >
                          {u.is_active ? <UserX size={13} /> : <UserCheck size={13} />}
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              className="w-7 h-7 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 grid place-items-center transition-colors"
                              title="Delete user"
                            >
                              <Trash2 size={13} />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete user?</AlertDialogTitle>
                              <AlertDialogDescription>This will soft-delete the user and revoke all their sessions. This cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteUser(u.user_id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-[hsl(var(--zt-beige-deep))]">
              <p className="text-xs text-[hsl(var(--zt-charcoal))]/50 font-heading">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40 hover:bg-[hsl(var(--zt-beige))] transition-colors">
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs font-heading font-semibold text-[hsl(var(--zt-teal))]">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40 hover:bg-[hsl(var(--zt-beige))] transition-colors">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
